<template>
  <config-base :show-plan-details="showPlanDetails" :plan-details-label="planDetailsLabel" class="opc-zwb" @view-details="handleViewDetails">
    <div slot="config" class="opc-zwb__config">
      <bought-license :type="planTypes.ZOOM_WHITEBOARD"/>
      <input-number-with-popover
        class="opc-zwb__quantity-selector"
        v-model="selectedNumber"
        size="large"
        controls-position="both"
        :min="minHosts"
        :max="maxHosts"
        @change="changeLicense"
        :decrease-tips="decreaseTips"
        :unit="$t('billing.planform.unit.license')"
        :label="trackingPlanName + ' Select the number of users'" />
      <alert v-if="mx_downSellLicenseErrorMessage" class="additional-template__alert" type="info">
        <span v-html="mx_downSellLicenseErrorMessage" />
      </alert>
      <license-tip ref="licenseTip" v-else :type="'zwb'" :number="selectedNumber" :additional-spans="additionalSpans">
      </license-tip>
      <config-cycle
        v-model="billingCycle"
        :billing-cycle-list="billingCycleList"
        @switchCycleAction="switchCycleAction"
        :show-cycle-alert="showCycleAlert"
      />
      <preview-payment class="opc-zwb__preview-payment" />
    </div>
    <template #footer v-if="canRmv">
      <zm-button type="link" ghost @click="removePlanByKey('ZOOM_WHITEBOARD')" :aria-label="'remove ' + trackingPlanName + ' plan'">
        {{ $t('billing.common.remove') }}
      </zm-button>
    </template>
  </config-base>
</template>

<script type="text/jsx">
import _ from 'lodash';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import plansMixin from '@/plans/mx/plan';
import ConfigBase from '@/components/config-base';
import commonMixin from '@/plans/ZOOM_WHITEBOARD/mixin';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import { trackingPlanTypes, planTypes } from '@/common/constants';
import ConfigCycle from '@/components/service/config-billing-cycle/index';
import LicenseTip from '@/plans/ZOOM_MEETING/license-tip.vue';
import Alert from '@/components/zoom-ui-plus/alert/alert.vue';
import AddonMixin from '@/plans/mx/addon';
import mxPlanDetails from '../dialog/mx-plan-details';
import PreviewPayment from '@/views/checkout/payment/preview-payment';
import BoughtLicense from '@/views/config/shopping-cart/prorated/bought-license.vue';

const ConfigBaseComp = {
  name: 'zwb-base',
  extends: ConfigBase,
  mixins: [commonMixin],
  props: {
    showPlanDetails: Boolean,
    planDetailsLabel: String,
  },
  data() {
    return {
      showLearnMore: false,
    };
  },
};

export default {
  mixins: [plansMixin, commonMixin, AddonMixin, mxPlanDetails],
  components: {
    BoughtLicense,
    PreviewPayment,
    ConfigCycle,
    InputNumberWithPopover,
    ConfigBase: ConfigBaseComp,
    LicenseTip,
    Alert
  },
  data() {
    return {
      selectedNumber: 1,
      billingCycle: '',
      trackingPlanName: trackingPlanTypes.ZOOM_WHITEBOARD,
      showCycleAlert: true,
      planTypes,
    };
  },
  computed: {
    ...mapGetters(['getSelectedPlansMap', 'getSelectedPlans', 'planMapFlat', 'getUserCurrentSubscriptions', 'getCurrentBasePlanInfo', 'getTypeGroupSelectedPlans', 'boughtPlans']),
    planList() {
      return this.planMapFlat.filter((p) => {
        const isCurrentPlanType = p.type === this.planType;
        const isCurrentCategory = p.category === this.selectedPlan.category
        return isCurrentPlanType && isCurrentCategory
      });
    },
    selectedPlan() {
      return this.getTypeGroupSelectedPlans[this.planType]?.[0];
    },
    minHosts() {
      return 1;
    },
    maxHosts() {
      return 99;
    },
    decreaseTips() {
      return '';
    },
    zwbAsBasePlan() {
      return this.getCurrentBasePlanInfo.planType === this.planType;
    },
    currentPlanCycleDepFirstPlan() {
      const dependCyclePlanList = _.get(this.planList, [0, 'billingCycleDependBasePlanTypeList'], []);

      return _.chain(this.getUserCurrentSubscriptions)
        .filter((p) => _.includes(dependCyclePlanList, p.type))
        .orderBy((p) => p.cycleValue, 'desc')
        .get(0)
        .value();
    },
    depCurrentAllAddonPlanList() {
      return _.filter(this.getUserCurrentSubscriptions,
        (p) => _.includes(p.dependBasePlanTypeList, this.planType));
    },
    disabledForBulkLicense() {
      return () => false;
    },
    disabledForBulkLicenseTips() {
      return this.$t('billing.error.buld_acount_license');
    },
    canRmv() {
      return false;
    },
    showPlanDetails() {
      return true;
    },
    planDetailsLabel() {
      return '';
    },
    additionalSpans() {
      return {};
    }
  },
  methods: {
    ...mapMutations(['updateSelectPlan']),
    ...mapActions(['switchBillingCycleAction']),
    changeLicense(newLicense,oldLicense) {
      this.selectedNumber = newLicense;
      this.updateSelectPlan({
        plan_code: this.selectedPlan?.plan_code,
        cartInfo: { selected_number: newLicense },
      });
      this.mx_validDownSellLicense(newLicense)

      this.$tracking.buyInteraction(
          `number of licenses`,
          `${this.trackingPlanName} - ${newLicense > oldLicense ? 'increase' : 'decrease'} - ${newLicense}`,
      );

      if(newLicense === this.maxHosts){
        this.$tracking.buyInteraction(
          'banner - product level',
          `${this.trackingPlanName} - the maximum amount is ${this.maxHosts} licenses - impression`,
        );
      }
    },
    async switchCycleAction(oldPlan, newPlan) {
      // when the current license less than max, get current license, otherwise get maxHosts
      const quantity = this.selectedNumber <= this.maxHosts ? this.selectedNumber : this.maxHosts;
      const newPlanInfo = { ...newPlan, cartInfo: { selected_number: quantity } };

      await this.switchBillingCycleAction([
        oldPlan?.plan_code,
        newPlanInfo,
      ]);

      // event tracking
      const billingCycle = this.selectedPlan?.billing_cycle;
      billingCycle && this.$tracking.addOnBuyInteraction(
        `${this.trackingPlanName} billing cycle`,
        this.$t(`billing.billing_cycle.${billingCycle}`)?.toLowerCase(),
      );
    },
    async handleViewDetails() {
      await this.showPlanDetailDialog();
    }
  },
  created() {
    const selectedPlan = this.selectedPlan;

    if (selectedPlan) {
      this.billingCycle = selectedPlan.billing_cycle;
      this.selectedNumber = selectedPlan.cartInfo?.selected_number;

      // refresh page
      this.$nextTick(() => {
        this.mx_validDownSellLicense(this.selectedNumber);
      });
    }
  },
  mounted(){
    this.$refs?.licenseTip?.$el?.addEventListener('click', (e) => {
      if (e?.target?.tagName === 'A') {
        this.$tracking.buyInteraction(
          'banner - product level',
          `${this.trackingPlanName} - the maximum amount is ${this.maxHosts} licenses - contact sales`,
        );
      }
    });
  }
};
</script>

<style lang="scss">
.opc-zwb {
  &__quantity-selector{
    margin-bottom: 10px;
  }

  &__preview-payment {
    margin-top: 24px;
  }

}
</style>
